import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import { Input, Rate } from "antd";
import { CheckCircleFilled, InfoCircleOutlined, LikeOutlined, QuestionCircleOutlined, RightOutlined } from "@ant-design/icons";
import CommonStyles from "@components/CommonStyles";
import themeColor from "@base/themes/colorTheme";
import useCommonSettings from "@base/hooks/useCommonSettings";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { IAppointment } from "@base/modules/appointment/appointment.interface";
import { IAppointmentRating, IRatingComment } from "@base/modules/appointmentRating/appointmentRating.interface";
import appointmentRatingService from "@base/modules/appointmentRating/appointmentRatingService";
import { getRatingView, getSurveyCode, surveyStorage, surveyStorageId } from "@base/modules/checkoutSurvey/checkoutSurveyRules";
import CheckoutSurveyModal from "./CheckoutSurveyModal";
import SurveyReviewModal from "./SurveyReviewModal";
import ContactHelpModal from "../ContactHelpModal";
import DetailCard from "../DetailCard";

const MAX_COMMENT_LENGTH = 255;

interface IProps {
  appointment: IAppointment;
}

/**
 * The rating block of a completed appointment: survey invite, stars with a comment and suggestion
 * chips, or the saved rating. With a checkout survey the survey comes first, its answers stay on this
 * browser until the stars are given, and there is no comment box - the survey already asked for one.
 */
export default function AppointmentRatingSection({ appointment }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { hotline } = useCommonSettings();

  const surveyCode = useMemo(() => getSurveyCode(appointment), [appointment]);
  const storageId = surveyStorageId(appointment);
  const isCompletedAndPaid = appointment.status === "COMPLETED" && appointment.payment_status !== false;

  const [rating, setRating] = useState<IAppointmentRating>();
  const [ratingState, setRatingState] = useState<"loading" | "ready" | "failed">("loading");
  // Mirrors the stored survey, and stands in for it when the browser will not store anything.
  const [pendingSurvey, setPendingSurvey] = useState<string | undefined>(() => surveyStorage.getPending(storageId));
  const [hasLocalReview, setHasLocalReview] = useState<boolean>(() => !!surveyStorage.getReview(storageId));
  const [point, setPoint] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [suggestions, setSuggestions] = useState<IRatingComment[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  // A second click lands before the button re-renders disabled; the ref stops it creating a second rating.
  const isSendingRef = useRef<boolean>(false);
  const [isSurveyOpen, setIsSurveyOpen] = useState<boolean>(false);
  const [isReviewOpen, setIsReviewOpen] = useState<boolean>(false);
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState<boolean>(false);

  //! function
  const loadRating = useCallback(async () => {
    if (!appointment.id || !isCompletedAndPaid) {
      return;
    }

    try {
      setRatingState("loading");
      const resp = await appointmentRatingService.getByAppointment(appointment.id);
      // Only a rating that belongs to this appointment counts.
      const belongs = !resp.data?.appointment_id || String(resp.data.appointment_id) === String(appointment.id);
      setRating(belongs ? resp.data : undefined);
      setRatingState("ready");
    } catch (error) {
      // "Not rated yet" may come back as a 404.
      if ((error as AxiosError)?.response?.status === 404) {
        setRating(undefined);
        setRatingState("ready");
      } else {
        setRatingState("failed");
      }
    }
  }, [appointment.id, isCompletedAndPaid]);

  useEffect(() => {
    setRating(undefined);
    setPoint(0);
    setComment("");
    loadRating();
  }, [loadRating]);

  useEffect(() => {
    setPendingSurvey(surveyStorage.getPending(storageId));
    setHasLocalReview(!!surveyStorage.getReview(storageId));
  }, [storageId]);

  const view = getRatingView({ appointment, surveyCode, rating, isSurveyDoneHere: !!pendingSurvey || hasLocalReview });
  // A survey appointment has no comment box: the patient's remarks are the survey's last question.
  const hasSurvey = !!surveyCode || !!rating?.survey || hasLocalReview;
  const canReview = !!rating?.survey || hasLocalReview;

  // Suggestions follow the stars, and only where there is a comment box to fill.
  useEffect(() => {
    if (view !== "open" || hasSurvey || !point) {
      setSuggestions([]);
      return;
    }

    let ignore = false;

    appointmentRatingService
      .getListRatingComment(i18n.resolvedLanguage, point)
      .then((resp) => !ignore && setSuggestions(Array.isArray(resp.data) ? resp.data : []))
      // Only suggestions - a failure just leaves none.
      .catch(() => !ignore && setSuggestions([]));

    return () => {
      ignore = true;
    };
  }, [view, hasSurvey, point, i18n.resolvedLanguage]);

  const warnTooLong = () =>
    dispatch(setAlertMessage({ type: "warning", message: t("Notification"), description: t("appointmentDetail.commentTooLong") }));

  const lines = comment.split("\n").map((line) => line.trim());
  const isSuggestionPicked = (text?: string) => !!text && lines.includes(text.trim());

  // A suggestion adds its line to what was typed, and removes it again on a second click.
  const toggleSuggestion = (text?: string) => {
    if (!text) {
      return;
    }

    if (isSuggestionPicked(text)) {
      setComment(
        comment
          .split("\n")
          .filter((line) => line.trim() !== text.trim())
          .join("\n"),
      );
      return;
    }

    const next = comment.trim() ? `${comment.replace(/\s+$/, "")}\n${text}` : text;

    if (next.trim().length > MAX_COMMENT_LENGTH) {
      warnTooLong();
      return;
    }

    setComment(next);
  };

  const onChangeComment = (value: string) => {
    if (value.trim().length > MAX_COMMENT_LENGTH) {
      warnTooLong();
      return;
    }

    setComment(value);
  };

  const onSurveyCompleted = (survey: string, review: Parameters<typeof surveyStorage.setReview>[1]) => {
    setIsSurveyOpen(false);
    setPendingSurvey(survey);
    setHasLocalReview(true);
    surveyStorage.setPending(storageId, survey);
    surveyStorage.setReview(storageId, review);
  };

  const trimmedComment = comment.trim();
  const isValid = view === "open" && ratingState === "ready" && point >= 1 && (hasSurvey || (trimmedComment.length > 0 && trimmedComment.length <= MAX_COMMENT_LENGTH));

  const onSubmit = async () => {
    if (!isValid || isSendingRef.current) {
      return;
    }

    try {
      isSendingRef.current = true;
      setIsSending(true);
      await appointmentRatingService.createAppointmentRating({
        appointment_id: appointment.id,
        point,
        comment: hasSurvey ? "" : trimmedComment,
        ...(pendingSurvey ? { survey: pendingSurvey } : {}),
      });

      surveyStorage.clearPending(storageId);
      setPendingSurvey(undefined);
      setIsSuccessOpen(true);
      loadRating();
    } catch (error) {
      // Survey answers stay stored, so sending again does not mean answering again.
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("Failed") + "!" }));
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  //! render
  if (!isCompletedAndPaid || (ratingState === "ready" && view === "hidden")) {
    return null;
  }

  const helpButton = (
    <CommonStyles.ButtonUI type="link" className="!p-0 h-auto font-medium" icon={<QuestionCircleOutlined />} onClick={() => setIsHelpOpen(true)}>
      {t("appointmentDetail.help")}
    </CommonStyles.ButtonUI>
  );

  const title = (
    <span className="flex items-center gap-2">
      <LikeOutlined className="text-common-primary" />
      {t("appointmentDetail.ratingTitle")}
    </span>
  );

  const renderBody = () => {
    if (ratingState === "loading") {
      return (
        <div className="relative min-h-[80px]">
          <CommonStyles.SpinnerUI isSpinning />
        </div>
      );
    }

    // Without knowing whether it was rated, sending could create a second rating.
    if (ratingState === "failed") {
      return (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-['Inter'] text-common-subtext">{t("appointmentDetail.ratingLoadFailed")}</span>
          <CommonStyles.ButtonUI className="h-9 font-medium" ghost onClick={loadRating}>
            {t("Retry")}
          </CommonStyles.ButtonUI>
        </div>
      );
    }

    if (view === "survey") {
      return (
        <div className="flex flex-col gap-3 p-4 rounded-xl bg-common-primary50">
          <span className="text-common-maintext text-base font-semibold font-['Inter']">{t("checkoutSurvey.inviteTitle")}</span>
          <span className="text-common-subtext text-sm font-['Inter']">{t("checkoutSurvey.inviteDescription")}</span>
          <div>
            <CommonStyles.ButtonUI type="primary" className="h-10 font-medium" onClick={() => setIsSurveyOpen(true)}>
              {t("checkoutSurvey.start")}
            </CommonStyles.ButtonUI>
          </div>
        </div>
      );
    }

    const isOpen = view === "open";
    const savedComment = rating?.comment?.trim();

    return (
      <div className="flex flex-col items-center gap-4 pt-3 border-0 border-t border-solid border-common-divider">
        <span className="text-common-subtext text-sm lg:text-base font-['Inter']">{t("appointmentDetail.ratingQuestion")}</span>

        <Rate
          disabled={!isOpen}
          value={view === "rated" ? Number(rating?.point ?? 0) : point}
          style={{ fontSize: 40, color: themeColor.yellow500 }}
          onChange={setPoint}
        />

        {view === "expired" ? (
          <span className="flex items-center gap-2 text-sm font-['Inter'] text-common-negative">
            <InfoCircleOutlined />
            {t("appointmentDetail.ratingExpired")}
          </span>
        ) : null}

        {!hasSurvey && (isOpen || view === "expired") ? (
          <div className="w-full flex flex-col gap-3">
            <Input.TextArea
              value={comment}
              disabled={!isOpen}
              placeholder={t("appointmentDetail.ratingPlaceholder")}
              autoSize={{ minRows: 4, maxRows: 8 }}
              className="!w-full !bg-common-background !border-0 !rounded-lg !text-sm lg:!text-base"
              onChange={(event) => onChangeComment(event.target.value)}
            />

            {isOpen && suggestions.length ? (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion) => {
                  const isPicked = isSuggestionPicked(suggestion.description);

                  return (
                    <button
                      key={suggestion.id ?? suggestion.description}
                      type="button"
                      onClick={() => toggleSuggestion(suggestion.description)}
                      className={`px-3 py-1.5 rounded-xl border-0 text-sm font-['Inter'] cursor-pointer transition-colors ${
                        isPicked ? "bg-common-primary text-common-white" : "bg-common-background text-common-subtext hover:bg-common-primary50"
                      }`}
                    >
                      {suggestion.description}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        {view === "rated" && !hasSurvey && savedComment ? (
          <div className="w-full p-3 rounded-lg bg-common-background text-sm lg:text-base font-['Inter'] text-common-maintext whitespace-pre-line">
            {savedComment}
          </div>
        ) : null}

        {hasSurvey && canReview ? (
          <button
            type="button"
            onClick={() => setIsReviewOpen(true)}
            className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-solid border-common-primary bg-common-primary50 cursor-pointer text-left"
          >
            <span className="flex flex-col">
              <span className="text-xs font-['Inter'] text-common-subtext">{t("checkoutSurvey.completed")}</span>
              <span className="text-sm font-medium font-['Inter'] text-common-primary">{t("checkoutSurvey.viewAnswers")}</span>
            </span>
            <RightOutlined className="text-common-primary" />
          </button>
        ) : null}

        {isOpen || view === "expired" ? (
          <div className="w-full flex justify-end">
            <CommonStyles.ButtonUI type="primary" className="h-10 px-6 font-medium" disabled={!isValid} loading={isSending} onClick={onSubmit}>
              {t("appointmentDetail.submitRating")}
            </CommonStyles.ButtonUI>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <DetailCard title={title} extra={helpButton}>
      <span className="text-common-subtext text-sm font-['Inter']">{t("appointmentDetail.ratingWindow")}</span>

      {renderBody()}

      {isSurveyOpen && surveyCode ? (
        <CheckoutSurveyModal open onClose={() => setIsSurveyOpen(false)} surveyCode={surveyCode} onCompleted={onSurveyCompleted} />
      ) : null}

      {isReviewOpen ? (
        <SurveyReviewModal onClose={() => setIsReviewOpen(false)} storageId={storageId} survey={rating?.survey ?? pendingSurvey} />
      ) : null}

      {isHelpOpen ? <ContactHelpModal open onClose={() => setIsHelpOpen(false)} /> : null}

      {isSuccessOpen ? (
        <CommonStyles.ModalConfirmationUI
          open
          toggle={() => setIsSuccessOpen(false)}
          type="normal"
          width={420}
          title={null}
          footer={
            <CommonStyles.ButtonUI className="w-full h-11 font-medium" type="primary" onClick={() => setIsSuccessOpen(false)}>
              {t("Close")}
            </CommonStyles.ButtonUI>
          }
          content={
            <div className="flex flex-col items-center gap-3 pt-4 text-center">
              <CheckCircleFilled className="text-6xl text-common-positive" />
              <span className="text-lg font-semibold font-['Inter'] text-common-maintext">{t("appointmentDetail.ratingSuccessTitle")}</span>
              <span className="text-sm font-['Inter'] text-common-subtext">{t("appointmentDetail.ratingSuccessMessage", { hotline })}</span>
            </div>
          }
        />
      ) : null}
    </DetailCard>
  );
}
